import React, { useState, useEffect } from "react";
import axios from "axios";
import CountUp from "react-countup";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Mail,
  Users,
  Settings,
  BarChart3,
  ArrowRight,
  ShieldCheck,
  LogOut,
  Sparkles,
  Send,
  MailOpen,
  Activity,
  MousePointerClick,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { signOut } from "firebase/auth";
import auth from "../../../../firebase.init";

const EmailMerketingDash = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  
  const [stats, setStats] = useState({
    totalContacts: 0,
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.email) return;
      try {
        const [userTemplatesRes, usersRes, trackingRes] = await Promise.all([
          axios
            .get(`https://emailserverjuly.shahlimon.online/user-email-templates?ownerEmail=${user.email}`)
            .catch(() => ({ data: [] })),
          axios
            .get(`https://emailserverjuly.shahlimon.online/users?ownerEmail=${user.email}`)
            .catch(() => ({ data: [] })),
          axios
            .get(`https://emailserverjuly.shahlimon.online/api/analytics/detailed-logs?ownerEmail=${user.email}`)
            .catch(() => ({ data: { trackingEvents: {} } })),
        ]);

        const manualUsers = Array.isArray(userTemplatesRes.data)
          ? userTemplatesRes.data
          : [];
        const registeredUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
        const allUsers = [...manualUsers, ...registeredUsers];

        let logs = [];
        allUsers.forEach((u) => {
          if (u.messageLogs && Array.isArray(u.messageLogs)) {
            logs = [...logs, ...u.messageLogs];
          }
        });

        const trackingEventsMap = trackingRes.data?.trackingEvents || {};

        let sent = logs.length;
        let opened = 0;
        let clicked = 0;

        logs.forEach((log) => {
          const trackingId = log.trackingId;
          const events =
            trackingId && trackingEventsMap[trackingId]
              ? trackingEventsMap[trackingId]
              : [];
          const isClicked = events.some((ev) => ev.eventType === "clicked");
          const isOpened = events.some((ev) => ev.eventType === "opened") || isClicked;
          
          if (isOpened) opened++;
          if (isClicked) clicked++;
        });

        setStats({
          totalContacts: allUsers.length,
          totalSent: sent,
          totalOpened: opened,
          totalClicked: clicked,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoadingStats(false);
      }
    };

    if (user?.email) {
      fetchStats();
    }
  }, [user]);

  const chartData = [
    { name: "Total Contacts", value: stats.totalContacts, color: "#3b82f6" },
    { name: "Total Sent", value: stats.totalSent, color: "#10b981" },
    { name: "Total Opened", value: stats.totalOpened, color: "#8b5cf6" },
    { name: "Total Clicked", value: stats.totalClicked, color: "#f59e0b" },
  ];

  const handleSignout = () => {
    signOut(auth)
      .then(() => {
        navigate("/login");
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  const cards = [
    {
      title: "Email Analytics",
      desc: "Track open rates, clicks, and campaign performance in real-time.",
      icon: <BarChart3 size={28} />,
      color: "primary",
      link: "/admin/email-analytics",
      highlight: true,
    },
    {
      title: "Email Templates",
      desc: "Design and manage custom AI-powered email templates.",
      icon: <Mail size={28} />,
      color: "info",
      link: "/admin/email-templates",
      highlight: false,
    },
    {
      title: "Audience Manager",
      desc: "Add, segment, and manage your marketing contacts.",
      icon: <Users size={28} />,
      color: "success",
      link: "/admin/user-email-marketing",
      highlight: false,
    },
    {
      title: "Setting Configuration",
      desc: "Configure your email delivery settings and Gemini API Keys.",
      icon: <Settings size={28} />,
      color: "danger",
      link: "/admin/user-marketing-smpt",
      highlight: false,
    },
  ];

  return (
    <div className="min-vh-100 bg-dark-main text-white py-5 px-3 px-lg-5">
      <div className="container-fluid max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-5 gap-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="d-flex align-items-center gap-3 mb-2">
              <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-lg">
                <Sparkles size={24} />
              </div>
              <h1 className="fw-bold fs-2 mb-0 tracking-tight text-white">Overview</h1>
            </div>
            <p
              className="text-secondary mb-0 mt-2 fs-6"
              style={{ maxWidth: "600px" }}
            >
              Scale your outreach with AI-generated content and precision
              tracking. Manage everything from delivery settings to performance
              analytics.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <button
              onClick={handleSignout}
              className="btn btn-dark border border-secondary-subtle rounded-pill d-flex align-items-center gap-2 px-4 py-2 hover-bg-danger transition-all"
            >
              <LogOut size={16} />
              <span className="fw-medium fs-6">Sign Out</span>
            </button>
          </motion.div>
        </div>

        {/* Overview Stats Section */}
        <div className="mb-5">
          <div className="row g-4 mb-4">
            {/* 4 Stat Cards */}
            <div className="col-12 col-md-3">
              <div className="dashboard-card p-4 rounded-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-secondary fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>Total Contacts</p>
                    <h3 className="fw-bolder text-white mb-0">
                      {loadingStats ? "..." : <CountUp end={stats.totalContacts} separator="," duration={2} />}
                    </h3>
                  </div>
                  <div className="p-2 rounded-3 bg-primary-soft text-primary">
                    <Users size={24} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="dashboard-card p-4 rounded-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-secondary fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>Total Sent Emails</p>
                    <h3 className="fw-bolder text-white mb-0">
                      {loadingStats ? "..." : <CountUp end={stats.totalSent} separator="," duration={2} />}
                    </h3>
                  </div>
                  <div className="p-2 rounded-3 bg-success-soft text-success">
                    <Send size={24} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="dashboard-card p-4 rounded-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-secondary fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>Total Open Emails</p>
                    <h3 className="fw-bolder text-white mb-0">
                      {loadingStats ? "..." : <CountUp end={stats.totalOpened} separator="," duration={2} />}
                    </h3>
                  </div>
                  <div className="p-2 rounded-3 bg-info-soft text-info" style={{ backgroundColor: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
                    <MailOpen size={24} color="#8b5cf6" />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="dashboard-card p-4 rounded-4 h-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-secondary fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>Click Count</p>
                    <h3 className="fw-bolder text-white mb-0">
                      {loadingStats ? "..." : <CountUp end={stats.totalClicked} separator="," duration={2} />}
                    </h3>
                  </div>
                  <div className="p-2 rounded-3 bg-warning-soft text-warning" style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>
                    <MousePointerClick size={24} color="#f59e0b" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bar Chart Section */}
          <div className="dashboard-card p-4 rounded-4">
            <h5 className="fw-bold text-white mb-4">Engagement Overview</h5>
            <div style={{ height: "300px" }}>
              {loadingStats ? (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-secondary">
                  Loading chart...
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #1e293b", borderRadius: "8px" }}
                      itemStyle={{ fontWeight: 500, color: "#fff" }}
                      cursor={{ fill: "rgba(255,255,255,0.05)" }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="row g-4">
          {cards.map((card, index) => (
            <div
              key={index}
              className={
                card.highlight ? "col-12 col-xl-12" : "col-12 col-md-6 col-xl-4"
              }
            >
              <motion.div
                className={`h-100 dashboard-card p-4 rounded-4 position-relative overflow-hidden ${card.highlight ? "highlight-card" : "standard-card"}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                whileHover={{ y: -4, scale: 1.01 }}
              >
                {/* Background Glow for highlight */}
                {card.highlight && (
                  <div
                    className="position-absolute top-0 end-0 rounded-circle bg-primary opacity-25 blur-3xl"
                    style={{
                      width: "150px",
                      height: "150px",
                      filter: "blur(60px)",
                      transform: "translate(30%, -30%)",
                    }}
                  />
                )}

                <div className="d-flex flex-column h-100 position-relative z-1">
                  <div className="d-flex align-items-start justify-content-between mb-4">
                    <div
                      className={`icon-box rounded-3 d-flex align-items-center justify-content-center text-${card.color} bg-${card.color}-soft`}
                      style={{ width: "48px", height: "48px" }}
                    >
                      {card.icon}
                    </div>
                    {card.highlight && (
                      <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-3 py-2 d-flex align-items-center gap-2">
                        <ShieldCheck size={14} />
                        <span className="fw-medium">Performance Ready</span>
                      </span>
                    )}
                  </div>

                  <div className="flex-grow-1">
                    <h3 className="fw-bold fs-4 mb-2 text-white">
                      {card.title}
                    </h3>
                    <p
                      className="text-secondary fs-6 lh-base mb-4"
                      style={{ maxWidth: card.highlight ? "80%" : "100%" }}
                    >
                      {card.desc}
                    </p>
                  </div>

                  <div className="mt-auto pt-4 border-top border-secondary-subtle d-flex justify-content-between align-items-center">
                    <Link
                      to={card.link}
                      className={`text-decoration-none fw-semibold d-inline-flex align-items-center gap-2 ${card.highlight ? "text-primary" : "text-white"} hover-gap-expanded transition-all`}
                    >
                      {card.highlight
                        ? "Open Analytics Dashboard"
                        : "Manage Module"}
                      <ArrowRight size={18} className="arrow-icon" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      <style jsx="true">{`
        .bg-dark-main {
          background-color: #020617; /* Slate 950 */
        }
        .text-secondary {
          color: #94a3b8 !important; /* Slate 400 */
        }
        .border-secondary-subtle {
          border-color: #1e293b !important; /* Slate 800 */
        }
        .max-w-7xl {
          max-width: 1280px;
        }

        .dashboard-card {
          background-color: #0f172a; /* Slate 900 */
          border: 1px solid #1e293b; /* Slate 800 */
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .dashboard-card:hover {
          border-color: #334155; /* Slate 700 */
          box-shadow:
            0 10px 15px -3px rgba(0, 0, 0, 0.2),
            0 4px 6px -2px rgba(0, 0, 0, 0.1);
        }

        .highlight-card {
          background: linear-gradient(145deg, #0f172a 0%, #172033 100%);
          border-color: rgba(59, 130, 246, 0.3); /* Blue border */
        }
        .highlight-card:hover {
          border-color: rgba(59, 130, 246, 0.5);
        }

        .bg-primary-soft {
          background: rgba(59, 130, 246, 0.1);
        }
        .text-primary {
          color: #60a5fa !important;
        }

        .bg-info-soft {
          background: rgba(6, 182, 212, 0.1);
        }
        .text-info {
          color: #22d3ee !important;
        }

        .bg-success-soft {
          background: rgba(16, 185, 129, 0.1);
        }
        .text-success {
          color: #34d399 !important;
        }

        .bg-danger-soft {
          background: rgba(239, 68, 68, 0.1);
        }
        .text-danger {
          color: #f87171 !important;
        }

        .hover-bg-danger:hover {
          background-color: rgba(239, 68, 68, 0.1) !important;
          color: #f87171 !important;
          border-color: rgba(239, 68, 68, 0.3) !important;
        }

        .hover-gap-expanded:hover .arrow-icon {
          transform: translateX(4px);
        }
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default EmailMerketingDash;
